import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const HeaderContainer = styled.header`
    z-index: 1000;
    background-color: ${({ theme }) => theme.card}cc; /* 80% opacity for glassmorphism */
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    height: 64px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    /*
        Grade de três colunas em vez de flex com space-between.

        A marca precisa ficar no CENTRO do cabeçalho, e com space-between isso é impossível: as
        laterais têm larguras diferentes — a direita cresce com o nome e o e-mail do usuário — então
        o meio escorregaria conforme quem está logado.

        Com a mesma fração nas duas pontas e auto no meio, as laterais reservam espaço igual e o
        centro é centro de verdade, qualquer que seja o conteúdo das bordas.

        O minmax(0, 1fr) não é preciosismo: 1fr sozinho tem mínimo AUTOMÁTICO, ou seja, não encolhe
        abaixo do próprio conteúdo. Um usuário com e-mail longo faria a coluna da direita crescer
        além da fração dela e empurraria a marca para a esquerda — o centro passaria a depender de
        quem está logado. Com mínimo zero as duas pontas cedem igual.
    */
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    padding: 0 24px;
    transition: background-color 0.3s ease, border-color 0.3s ease;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
        padding: 0 16px;
    }
`;

export const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    /* Primeira coluna da grade: encostada à esquerda. */
    justify-self: start;
`;

/**
 * Coluna do meio — a marca.
 *
 * Existe como seção própria justamente para poder ocupar a coluna central da grade. Enquanto a logo
 * vivia dentro da `LeftSection`, ela era mais um item de uma fila alinhada à esquerda; não havia
 * como centralizá-la no cabeçalho sem tirá-la de lá.
 */
export const BrandSection = styled.div`
    display: flex;
    align-items: center;
    justify-self: center;
    /* Não deixa a marca ser comprimida quando as laterais crescem. */
    flex-shrink: 0;
`;

/**
 * A marca é um LINK para a página principal, não uma div com onClick.
 *
 * Ela já tinha `cursor: pointer` — foi desenhada para ser clicável — mas nada estava ligado, então
 * o cursor prometia uma ação que não existia.
 *
 * É um link de verdade (`Link` do react-router, que renderiza `<a href>`) e não um `<button>` por
 * dois motivos concretos: leitor de tela anuncia "link para a página inicial" em vez de um botão
 * genérico, e ctrl+clique / clique do meio abrem em nova aba — que é o que qualquer pessoa espera de
 * uma marca no topo. Um `<div onClick>` não dá nenhuma das duas coisas, e ainda fica fora da ordem
 * de tabulação.
 */
export const Logo = styled(Link)`
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    font-size: 18px;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.02em;
    cursor: pointer;
    transition: opacity 0.2s ease;
    /* Link herda sublinhado e a cor de link do browser; a marca não quer nenhum dos dois. */
    text-decoration: none;

    &:hover {
        opacity: 0.9;
    }

    /*
        Foco visível apenas por teclado. Sem isto o link fica sem indicação nenhuma de foco, e quem
        navega por Tab não sabe onde está — um outline:none global é uma das causas mais comuns disso.

        (Sem crase neste comentário: crase dentro de comentário CSS encerra o template literal do
        styled-components, e o erro sai como TS1005 numa linha visivelmente correta.)
    */
    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.primary};
        outline-offset: 4px;
        border-radius: 4px;
    }

    /*
        A logo agora é a imagem oficial da Hexium (hexágono vazado com HX), em PNG com canal alfa.

        O que saiu daqui: fundo em degradê petrol, raio de 10px e sombra. Aquilo era MOLDURA para o
        ícone anterior — um robô desenhado em SVG que não tinha forma própria. A logo real tem
        silhueta e cor próprias; enquadrá-la num quadrado colorido criaria um losango dentro de um
        quadrado e brigaria com a identidade em vez de apresentá-la.

        O object-fit contain garante que ela nunca distorce, qualquer que seja a proporção do
        arquivo — o atual é 282×232, que não é quadrado.
    */
    .logo-icon {
        width: 34px;
        height: 34px;
        object-fit: contain;
        display: block;
        flex-shrink: 0;
    }

    span {
        white-space: nowrap;
        /*
            Cor sólida, não degradê recortado. O nome da marca é o texto mais importante da tela e
            precisa de contraste estável; o background-clip text com dois tons fazia metade das
            letras chegarem ao tom mais claro, que é o de texto SECUNDÁRIO — legibilidade menor
            justamente na palavra que identifica o produto.
        */
        color: ${({ theme }) => theme.title};
    }

    @media (max-width: 480px) {
        span {
            display: none;
        }
    }
`;

export const PageInfo = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 16px;
    
    @media (max-width: 768px) {
        display: none;
    }
`;

export const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    /* Terceira coluna da grade: encostada à direita. */
    justify-self: end;
`;

export const UserContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    
    .user-name {
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
    }
    
    .user-email {
        font-size: 12px;
        color: ${({ theme }) => theme.textSecondary || theme.text};
        opacity: 0.7;
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

export const UserAvatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.primaryHover});
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.primaryOn};
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 2px 8px ${({ theme }) => theme.primary}30;
    border: 2px solid transparent;
    
    &:hover {
        transform: scale(1.05);
        border-color: ${({ theme }) => theme.primary}50;
    }
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
    }
`;

export const ProfileMenu = styled.div<{ $isOpen: boolean }>`
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: 240px;
    background-color: ${({ theme }) => theme.card || theme.background};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 6px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    z-index: 1001;
    transform-origin: top right;
    animation: menuSlideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    backdrop-filter: blur(12px);
    
    @keyframes menuSlideDown {
        from { opacity: 0; transform: scale(0.95) translateY(-10px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
    }
`;

export const MenuHeader = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    margin-bottom: 6px;
    
    .user-name {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin-bottom: 2px;
    }
    
    .user-email {
        font-size: 12px;
        color: ${({ theme }) => theme.textSidebar};
    }
`;

export const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    font-size: 14px;
    border-radius: 6px;
    transition: background-color 0.2s;
    text-align: left;
    
    &:hover {
        background-color: ${({ theme }) => theme.sidebarItemHover};
    }
    
    &.danger {
        color: ${({ theme }) => theme.error || theme.error};
        
        &:hover {
            background-color: ${({ theme }) => theme.errorBackground || '${({ theme }) => alpha(theme.error, 0.1)}'};
        }
    }
`;

export const MobileMenuButton = styled.button`
    display: none;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.text};
    padding: 8px;
    cursor: pointer;
    
    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
